// Sale model line
import proxy from "api/proxy";
import useChildStore from "store/childStore";
import useMainStore from "store/mainStore";
import funcs from "tools/functions";

const fix = (val, decimals = 4) => Number(Number(val).toFixed(decimals));

const getProduct = (value) => {
  const search = [
    "OR",
    [
      ["template.name", "ilike", `%${value}%`],
      ["template.salable", "=", true],
    ],
    [
      ["code", "ilike", `${value}%`],
      ["template.salable", "=", true],
    ],
  ];
  return search;
};

const getSalePrices = async (record) => {
  if (!record.product) return {};
  const parentRec = useMainStore.getState().record;
  const party = funcs.prepareRecord(parentRec.party);
  const store = useChildStore.getState().store;
  const line = {
    product: record.product.id,
    quantity: Number(store.quantity),
    sale: {
      party: party,
      invoice_party: party,
      price_list: parentRec?.price_list?.id,
    },
  };
  const unit = record.unit?.id ?? record.product["sale_uom."].id;
  const { data: result } = await proxy.methodInstance({
    model: "sale.line",
    method: "on_change_product",
    instance: line,
    args: [],
    ctx: {
      price_list: parentRec?.price_list?.id,
      uom: unit,
      party: parentRec?.party?.id,
      invoice_party: parentRec?.party?.id,
    },
  });

  const taxes = [];

  if (result?.taxes?.add) {
    for (const item of result.taxes.add) {
      taxes.push(item[1].id);
    }
  }
  line.taxes = taxes;
  line.quantity = Number(store.quantity) || 1;
  if (result) {
    result.unit_price ??= result.base_price;
    line.unit_price = result.unit_price;
  }

  const { data: unit_price_w_tax } = await proxy.methodInstance({
    model: "sale.line",
    method: "on_change_with_unit_price_w_tax",
    instance: line,
    args: [],
    ctx: {
      price_list: parentRec?.price_list?.id,
      uom: unit,
      party: parentRec?.party?.id,
      invoice_party: parentRec?.party?.id,
    },
  });
  return { result: result, price_w_tax: unit_price_w_tax, taxes: taxes };
};

const onChangeProduct = async (record, store) => {
  // const parentRecord = parentRec.record;
  const product = record?.product;

  if (product) {
    const unit = product["sale_uom."];
    const { result, price_w_tax, taxes } = await getSalePrices(record);
    if (product.packing_uom) store.packing_uom = product.packing_uom;
    const base_price = fix(result?.base_price);

    record.discount_rate = 0;
    record.discount_amount = 0;
    record.base_price = base_price;
    // record.unit_price = record.product.list_price;
    record.unit_price = fix(result?.unit_price);
    record.unit = unit;
    record.unit_price_w_tax = fix(price_w_tax);
    record.amount_w_tax = fix(price_w_tax);
    record.description = result?.description;
    store.description = product.description || product.name;
    // store.unit_price = result.unit_price.toString();
    // store.unit_price = record.product.list_price.toString();
    store.unit_price = fix(result?.unit_price).toString();
    store.base_price = fix(result?.base_price).toString();
    store.unit = unit?.id;
    store.taxes = unit?.id ? [["add", taxes]] : [];
  }
};

const onChangeUnit = async (record, store) => {
  const { unit_price, price_w_tax } = await getSalePrices(record);
  record.unit_price = fix(unit_price);
  record.unit_price_w_tax = fix(price_w_tax);
  store.unit_price = fix(unit_price).toString();
  store.unit_price_w_tax = fix(price_w_tax).toString();
};

const onChangeQty = async (record, store) => {
  const qty = Number(record.quantity);
  record.quantity = qty;
  const { result, price_w_tax } = await getSalePrices(record);
  if (qty && record.unit_price_w_tax) {
    record.base_price = fix(result?.base_price).toString();
    record.unit_price = fix(result?.unit_price).toString();
    record.unit_price_w_tax = fix(price_w_tax);
    store.unit_price = fix(result?.unit_price).toString();
    store.base_price = fix(result?.base_price).toString();

    if (record.discount) {
      const { amount, discount } = record;
      record.amount_w_tax = fix(amount * qty - (amount * qty * discount) / 100);
    } else {
      record.amount_w_tax = fix(record.unit_price_w_tax * qty);
    }
  }
};

const onChangeUnitPrice = async (record, store) => {
  if (record.quantity && record.unit_price) {
    const quantity = Number(String(record.quantity).replace(/,/g, ""));
    const unit_price = Number(String(record.unit_price).replace(/,/g, ""));
    const base_price = record.base_price
      ? Number(String(record.base_price).replace(/,/g, ""))
      : null;

    if (base_price && unit_price < base_price) {
      const discount_rate = ((base_price - unit_price) / base_price) * 100;
      record.discount_rate = fix(discount_rate);
      record.discount_amount = fix(base_price - unit_price);
      if (store) {
        store.discount_rate = fix(discount_rate).toString();
        store.discount_amount = fix(base_price - unit_price).toString();
      }
    } else if (base_price) {
      record.discount_rate = 0;
      record.discount_amount = 0;
      if (store) {
        store.discount_rate = "0";
        store.discount_amount = "0";
      }
    }

    if (record.discount) {
      const amount = unit_price - (unit_price * record.discount) / 100;
      record.unit_price_w_tax = fix(amount);
      record.amount_w_tax = fix(amount * quantity);
    } else {
      const { data: price_w_tax } = await proxy.methodCall({
        model: "sale.sale",
        method: "dash_get_amount_w_tax",
        args: [
          {
            product: record.product["id"],
            list_price: unit_price,
          },
        ],
      });
      record.unit_price_w_tax = fix(price_w_tax);
      record.amount_w_tax = fix(price_w_tax * quantity);
    }
  }
};

const searchUnit = (value, record) => {
  const dom = [["category", "=", record?.unit?.category]];
  if (value) {
    dom.push(["name", "ilike", `%${value}%`]);
  }
  return dom;
};

const onChangeDiscount = (record, store) => {
  const quantity = Number(record.quantity);
  const { base_price, discount_rate } = record;
  if (!base_price || !quantity || !discount_rate) return;
  const rate = Math.min(Number(discount_rate), 100);
  const dsct_amount = (base_price * rate) / 100;
  const unit_price_ = base_price - dsct_amount;
  record.discount_rate = fix(rate);
  record.discount_amount = fix(dsct_amount);
  record.unit_price = fix(unit_price_);
  if (store) store.unit_price = fix(unit_price_).toString();

  const product = record.product?.id ?? record.product;
  if (product) {
    const { data: price_w_tax } = proxy.methodCall({
      model: "sale.sale",
      method: "dash_get_amount_w_tax",
      args: [{ product, list_price: unit_price_ }],
    });
    record.unit_price_w_tax = fix(price_w_tax ?? unit_price_);
    record.amount_w_tax = fix((price_w_tax ?? unit_price_) * quantity);
  }
};

const getView = (config) => {
  const { modules_activated } = config;
  const saleFlex =
    Array.isArray(modules_activated) &&
    modules_activated.includes("sale_packing_unit");

  Array.prototype.insert = function (index, item) {
    this.splice(index, 0, item);
  };
  const DictCtxView = {
    form_action_add: "modal",
    model: "sale.line",
    row_selectable: false,
    table_action: ["edit", "add", "delete"],
    form_action: ["add", "delete", "edit"],
    target: "sale",
    selectable: true,
    webfields: {
      product: {
        type: "many2one",
        model: "product.product",
        recSearch: getProduct,
        withChange: onChangeProduct,
        dependents: [
          "unit",
          "unit_price",
          "unit_price_w_tax",
          "amount_w_tax",
          "description",
          "base_price",
        ],
        attrs: (() => {
          const baseAttrs = [
            "id",
            "list_price",
            "name",
            "sale_price_taxed",
            "sale_uom.rec_name",
            "description",
          ];
          if (saleFlex) {
            baseAttrs.push("packing_uom");
          }
          return baseAttrs;
        })(),
        required: true,
      },

      unit: {
        type: "many2one",
        model: "product.uom",
        recSearch: searchUnit,
        withChange: onChangeUnit,
        readOnly: true,
        attrs: ["id", "name", "category", "factor"],
      },

      quantity: {
        type: "numeric",
        withChange: onChangeQty,
        // required: true,
        decimalPlaces: 2,
        default: 1,
        whitoutOnBlur: true,
      },
      base_price: { type: "numeric", readOnly: true },
      unit_price: {
        type: "numeric",
        readOnly: false,
        decimalPlaces: 2,
        whitoutOnBlur: true,
      },
      unit_price_w_tax: { type: "numeric", readOnly: true },
      amount_w_tax: { type: "numeric", readOnly: true },
      description: { type: "char" },
      discount_rate: {
        type: "numeric",
        withChange: onChangeDiscount,
        search: true,
        decimalPlaces: 2,
        format: "percent",
        whitoutOnBlur: true,
      },
      discount_amount: { type: "numeric", readOnly: true, decimalPlaces: 2 },
    },
    webtree: [
      { name: "product", width: "40%" },
      { name: "unit", width: "10%" },
      { name: "quantity", width: "10%" },
      { name: "base_price" },
      { name: "unit_price", width: "10%" },
      { name: "amount_w_tax", width: "23%" },
    ],
    webform: [
      { name: "product" },
      { name: "unit" },
      { name: "quantity" },
      { name: "unit_price" },
      { name: "unit_price_w_tax" },
      { name: "amount_w_tax" },
      { name: "description" },
    ],
  };

  if (config && config.allow_discount) {
    DictCtxView["webtree"].insert(4, {
      name: "discount_rate",
      width: "4%",
      // format: "percent",
    });
    DictCtxView["webform"].insert(4, { name: "discount_rate" });
    DictCtxView["webtree"].insert(5, { name: "discount_amount", width: "4%" });
    DictCtxView["webform"].insert(5, { name: "discount_amount" });
  }
  if (config && config.allow_manual_pricing) {
    DictCtxView["webfields"]["unit_price"] = {
      type: "numeric",
      required: true,
      withChange: onChangeUnitPrice,
      decimalPlaces: 2,
      whitoutOnBlur: true,
      updateOnChange: true,
    };
  }
  return DictCtxView;
};

export default { ctxView: getView };
